<template>
  <!-- Menú "Dirigirse a..." de las tablas del dashboard del directivo: replica las
  opciones del Perfil de Egreso de careerList (CareerTable). Si se pasa matter_id
  antepone el Programa de la Asignatura, separado del bloque del Perfil de Egreso.
  Estos dashboards solo los ven directivos, por lo que no aplica el filtro de
  visibilidad por profesor/estudiante que hace CareerTable. -->
  <ButtonWrapperSmall
    :id_button="id_button"
    :tooltip_text="$t('careerOrPrograms.careerListView.goToAction')"
    class="noprint"
  >
    <template #popover-content-wrapper>
      <template v-if="matter_id">
        <b-button
          variant="primary"
          class="dropdown-class"
          v-b-tooltip.v-secondary.top.noninteractive="matter_program_tooltip"
          @click="redirectTo('MatterProgramView', { matter_id: matter_id })"
        >
          {{ matter_program_label }}
        </b-button>
        <hr v-if="egress_profile_id" class="navigation-separator" />
      </template>
      <template v-if="egress_profile_id">
        <b-button
          v-for="option in egress_profile_options"
          :key="`${id_button}-${option.key}`"
          variant="primary"
          class="dropdown-class"
          v-b-tooltip.v-secondary.top.noninteractive="option.tooltip"
          @click="
            redirectTo(option.route_name, {
              egress_profile_id: egress_profile_id,
            })
          "
        >
          {{ option.label }}
        </b-button>
      </template>
    </template>
  </ButtonWrapperSmall>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
import { computed, getCurrentInstance } from "vue";
// BootstrapVue a propósito: ButtonWrapperSmall es el popover estándar de la app y
// mantiene este menú idéntico al de careerList. Migrar junto con ese wrapper.
import ButtonWrapperSmall from "@/components/reusable/Buttons/Small/ButtonWrapperSmall";

const props = defineProps({
  // Debe ser único en el DOM: ButtonWrapperSmall resuelve el target del popover por id
  id_button: {
    type: String,
    required: true,
  },
  // Puede faltar si el backend aún no expone egress_profile en la fila; en ese
  // caso se omiten las opciones del Perfil de Egreso en vez de navegar a un id
  // undefined (vue-router no puede construir la ruta y la navegación falla).
  egress_profile_id: {
    type: [Number, String],
    default: null,
  },
  // Id del EgressProfileMatter. Si viene, se agrega el Programa de la Asignatura
  matter_id: {
    type: [Number, String],
    default: null,
  },
});

const { proxy } = getCurrentInstance();

const institution = computed(() => proxy.$store.getters.getInstitution);

const matter_program_label = computed(() =>
  proxy.$getVisibleNames("manual.matter_program", false, "Programa de Asignatura")
);

const matter_program_tooltip = computed(
  () =>
    `${proxy.$t("reusable.customFab.navigateTo")} "${matter_program_label.value}"`
);

// Computed-objeto a propósito: el template lo consume como unidad en un v-for, así
// que separar cada etiqueta en su propio computed no evitaría el re-render.
const egress_profile_options = computed(() => {
  const options = [
    {
      key: "egress_profile",
      route_name: "EgressProfileView",
      label: proxy.$getVisibleNames(
        "mesh.egressprofile",
        false,
        "Perfil de Egreso"
      ),
      tooltip: proxy.$t("careerOrPrograms.careerListView.goToProfile"),
    },
    matrixOption(1, "manual.matriz_1", "Matriz 1"),
    matrixOption(2, "manual.matriz_2", "Matriz 2"),
    matrixOption(3, "manual.matriz_3", "Matriz 3"),
  ];
  // Matriz 4 es exclusiva de duoc, igual que en CareerTable
  if (institution.value && institution.value.internal_use_id == "duoc.cl")
    options.push(matrixOption(4, "manual.matriz_4", "Matriz 4"));
  options.push({
    key: "study_plan",
    route_name: "StudyPlanView",
    label: proxy.$t("careerOrPrograms.careerListView.studyPlan"),
    tooltip: proxy.$t("careerOrPrograms.careerListView.goToStudyPlan"),
  });
  // Carga Académica va entre el Plan de Estudio y la Malla, igual que en CareerTable
  if (proxy.$usesAcademicLoad(institution.value))
    options.push({
      key: "academic_load",
      route_name: "AcademicLoadView",
      label: proxy.$getVisibleNames(
        "manual.academic_load",
        false,
        proxy.$t("careerOrPrograms.careerListView.academicLoad")
      ),
      tooltip: proxy.$t("careerOrPrograms.careerListView.goToAcademicLoad"),
    });
  options.push(
    {
      key: "curricular_mesh",
      route_name: "CurricularMeshView",
      label: proxy.$t("careerOrPrograms.careerListView.curricularMesh"),
      tooltip: proxy.$t("careerOrPrograms.careerListView.goToCurricularMesh"),
    }
  );
  return options;
});

function matrixOption(number, visible_name_key, default_name) {
  const label = proxy.$getVisibleNames(visible_name_key, false, default_name);
  return {
    key: `matrix_${number}`,
    route_name: `Matrix${number}View`,
    label: label,
    tooltip: proxy.$t("careerOrPrograms.careerListView.goToThe", {
      entity: label,
    }),
  };
}

function redirectTo(route_name, params) {
  proxy.$router.push({ name: route_name, params: params });
}
</script>

<style scoped>
/* Botones del popover: mismo formato que en CareerTable */
.dropdown-class {
  width: 100%;
  height: 35px !important;
  margin-bottom: 4px;
  border-radius: 4px;
  padding: 0;
  transition: background-color 0.3s, color 0.3s, box-shadow 0.3s;
}
.dropdown-class:hover {
  cursor: pointer;
}
/* Separa el Programa de la Asignatura del bloque del Perfil de Egreso */
.navigation-separator {
  margin: 2px 0 6px 0;
  border-top: 1px solid var(--kl-border-soft);
}
</style>
